import React from "react";
import { Link } from "react-router-dom";
import type { HelpBlock } from "@/content/help";

const calloutStyles = {
  info: "border-brand/30 bg-brand/10 text-white/85",
  tip: "border-brand/30 bg-brand/5 text-white/80",
  warn: "border-warn/30 bg-warn/10 text-warn/95",
};

export function HelpArticle({ blocks }: { blocks: HelpBlock[] }) {
  return (
    <article className="space-y-5">
      {blocks.map((block, index) => {
        switch (block.type) {
          case "paragraph":
            return (
              <p key={index} className="text-sm leading-relaxed text-white/75">
                {block.text}
              </p>
            );
          case "heading":
            return block.level === 3 ? (
              <h3
                key={index}
                className="text-base font-semibold text-white"
              >
                {block.text}
              </h3>
            ) : (
              <h2
                key={index}
                className="border-b border-white/10 pb-2 text-lg font-bold text-white"
              >
                {block.text}
              </h2>
            );
          case "formula":
            return (
              <div
                key={index}
                className="rounded-[14px] border border-white/10 bg-surface-elevated p-4"
              >
                <div className="text-sm font-semibold text-white">{block.label}</div>
                <div className="mt-2 font-mono text-sm text-brand">
                  {block.expression}
                </div>
                {block.note ? (
                  <p className="mt-2 text-xs text-white/55">{block.note}</p>
                ) : null}
              </div>
            );
          case "list":
            return block.ordered ? (
              <ol
                key={index}
                className="list-decimal space-y-2 pl-5 text-sm text-white/75"
              >
                {block.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            ) : (
              <ul
                key={index}
                className="list-disc space-y-2 pl-5 text-sm text-white/75"
              >
                {block.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            );
          case "table":
            return (
              <div key={index} className="overflow-x-auto rounded-[14px] ring-1 ring-white/10">
                <table className="w-full text-left text-sm">
                  <thead className="bg-surface-elevated text-white/65">
                    <tr>
                      {block.headers.map((header) => (
                        <th key={header} className="px-4 py-3 font-semibold">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-t border-white/10">
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-4 py-3 text-white/75 align-top"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "callout":
            return (
              <div
                key={index}
                className={`rounded-[14px] border px-4 py-3 text-sm ${calloutStyles[block.variant]}`}
              >
                {block.title ? (
                  <div className="mb-1 font-semibold">{block.title}</div>
                ) : null}
                <p className="leading-relaxed">{block.text}</p>
              </div>
            );
          default:
            return null;
        }
      })}
    </article>
  );
}

export function HelpLink({
  to,
  children,
  className = "",
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={`text-xs font-medium text-brand hover:text-emerald-200 underline-offset-2 hover:underline ${className}`}
    >
      {children}
    </Link>
  );
}
